import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Droplets, Milk, Factory, Boxes, Plus, X, Trash2, Package } from "lucide-react";
import { toast } from "sonner";

const INSUMO_LABELS = { caseina: "Caseína", base: "Base", cuajo: "Cuajo", sal: "Sal", cloruro: "Cloruro de calcio" };
const money = (n) => `$${(n ?? 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Inventario() {
  const [data, setData] = useState(null);
  const [entries, setEntries] = useState([]);
  const [insumosCat, setInsumosCat] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", kg: "", unit_cost: "", entry_date: new Date().toISOString().slice(0, 10), notes: "" });
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    const [inv, ent, params] = await Promise.all([
      api.get("/inventory"),
      api.get("/insumos-entries"),
      api.get("/parameters"),
    ]);
    setData(inv.data);
    setEntries(ent.data);
    setInsumosCat(params.data.correction_inputs || []);
  };
  useEffect(() => { load(); }, []);

  const submitEntry = async (e) => {
    e.preventDefault();
    if (!form.code || !form.kg || !form.unit_cost) return toast.error("Completa código, kg y costo");
    try {
      await api.post("/insumos-entries", { code: form.code, kg: parseFloat(form.kg), unit_cost: parseFloat(form.unit_cost), entry_date: form.entry_date, notes: form.notes });
      toast.success("Entrada de insumo registrada");
      setForm({ code: "", kg: "", unit_cost: "", entry_date: new Date().toISOString().slice(0, 10), notes: "" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const del = async () => {
    try {
      await api.delete(`/insumos-entries/${toDelete.id}`);
      toast.success("Entrada eliminada");
      setToDelete(null);
      load();
    } catch { toast.error("Error"); }
  };

  if (!data) return <div className="p-8 text-stone-500 dark:text-stone-400">Cargando…</div>;

  return (
    <div className="max-w-screen-2xl mx-auto p-6 lg:p-8 space-y-6" data-testid="inventario-page">
      <div>
        <h1 className="font-outfit font-bold text-3xl tracking-tight text-stone-900 dark:text-stone-100">Inventario</h1>
        <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">Materia prima, producto terminado e insumos disponibles en planta. Se actualiza al registrar producción, correcciones o ventas.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <KPI icon={Droplets} label="MP disponible" value={data.totals.mp_liters_available.toLocaleString("es-MX")} unit="L" tone="blue" />
        <KPI icon={Milk} label="Crema disponible" value={data.totals.cream_kg_available.toLocaleString("es-MX")} unit="kg" tone="amber" />
        <KPI icon={Factory} label="Cuajada disponible" value={data.totals.curd_kg_available.toLocaleString("es-MX")} unit="kg" tone="emerald" />
        <KPI icon={Boxes} label="Insumos disponibles" value={(data.totals.insumo_kg_available || 0).toLocaleString("es-MX")} unit="kg" tone="stone" />
      </div>

      <Table title="Materia Prima (lotes MP-)" rows={data.mp} columns={[
        { k: "folio", h: "Lote", highlight: true },
        { k: "supplier", h: "Proveedor" },
        { k: "date", h: "Fecha" },
        { k: "liters_received", h: "L recibidos", num: true },
        { k: "liters_used_production", h: "L en producción", num: true, class: "text-orange-700 dark:text-orange-400" },
        { k: "liters_sold", h: "L vendidos", num: true, class: "text-blue-700 dark:text-blue-400" },
        { k: "liters_available", h: "L disponibles", num: true, bold: true, class: "text-emerald-700 dark:text-emerald-400" },
        { k: "status", h: "Estado", badge: true },
      ]} />

      <Table title="Crema: producción + compra a proveedor (lotes CRE-/CREC-)" rows={data.cream} columns={[
        { k: "folio", h: "Lote", highlight: true },
        { k: "origin", h: "Origen", badge: true, badgeMap: ORIGIN_BADGE },
        { k: "from_mp", h: "Procedencia" },
        { k: "date", h: "Fecha" },
        { k: "kg_produced", h: "kg totales", num: true },
        { k: "kg_sold", h: "kg vendidos", num: true, class: "text-blue-700 dark:text-blue-400" },
        { k: "kg_available", h: "kg disponibles", num: true, bold: true, class: "text-emerald-700 dark:text-emerald-400" },
      ]} />

      <Table title="Cuajada: producción + compra a proveedor (lotes CUA-/CUAC-)" rows={data.curd} columns={[
        { k: "folio", h: "Lote", highlight: true },
        { k: "origin", h: "Origen", badge: true, badgeMap: ORIGIN_BADGE },
        { k: "from_mp", h: "Procedencia" },
        { k: "date", h: "Fecha" },
        { k: "kg_produced", h: "kg totales", num: true },
        { k: "kg_sold", h: "kg vendidos", num: true, class: "text-blue-700 dark:text-blue-400" },
        { k: "kg_available", h: "kg disponibles", num: true, bold: true, class: "text-emerald-700 dark:text-emerald-400" },
      ]} />

      {/* Insumos section */}
      <Card className="border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-stone-200 dark:border-stone-800 flex items-center gap-2">
          <Package className="w-4 h-4 text-stone-500" />
          <h3 className="font-outfit font-semibold text-stone-900 dark:text-stone-100">Insumos (caseína, base, cuajo, sal…)</h3>
          {!open && (
            <Button onClick={() => setOpen(true)} size="sm" data-testid="new-insumo-entry-btn" className="ml-auto bg-[#1E3A8A] hover:bg-[#1E40AF]">
              <Plus className="w-3.5 h-3.5 mr-1" /> Registrar entrada
            </Button>
          )}
        </div>

        {open && (
          <form onSubmit={submitEntry} className="p-5 border-b border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900/40 grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Insumo *</Label>
              <Select value={form.code} onValueChange={(v) => setForm({ ...form, code: v })}>
                <SelectTrigger data-testid="entry-code" className="mt-1"><SelectValue placeholder="Elegir insumo…" /></SelectTrigger>
                <SelectContent>
                  {insumosCat.length === 0 ? <SelectItem value="_none" disabled>Sin catálogo (crea en Parámetros)</SelectItem>
                    : insumosCat.map((i) => <SelectItem key={i.code} value={i.code}>{i.name || INSUMO_LABELS[i.code] || i.code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Kg *</Label><Input type="number" step="0.01" value={form.kg} onChange={(e) => setForm({ ...form, kg: e.target.value })} className="mt-1" data-testid="entry-kg" /></div>
            <div><Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">$ / kg *</Label><Input type="number" step="0.01" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} className="mt-1" data-testid="entry-cost" /></div>
            <div><Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Fecha</Label><Input type="date" value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} className="mt-1" /></div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}><X className="w-4 h-4" /></Button>
              <Button type="submit" size="sm" data-testid="entry-submit-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">Guardar</Button>
            </div>
          </form>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-100 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
              <tr>{["Insumo", "Comprado", "Usado en corrección", "Vendido", "Disponible", "Costo promedio"].map((h) => <th key={h} className="px-3 py-2.5 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">{h}</th>)}</tr>
            </thead>
            <tbody>
              {(data.insumo || []).length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-sm text-stone-500">Sin insumos registrados. Registra una entrada para empezar a controlarlos.</td></tr>
              ) : data.insumo.map((r, i) => (
                <tr key={i} className="border-b border-stone-100 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-900/50">
                  <td className="px-3 py-2 text-sm font-medium text-stone-900 dark:text-stone-100">{INSUMO_LABELS[r.code] || r.code}</td>
                  <td className="px-3 py-2 text-sm tabular-nums text-stone-700 dark:text-stone-300">{r.kg_purchased} kg</td>
                  <td className="px-3 py-2 text-sm tabular-nums text-orange-700 dark:text-orange-400">{r.kg_used_correction} kg</td>
                  <td className="px-3 py-2 text-sm tabular-nums text-blue-700 dark:text-blue-400">{r.kg_sold} kg</td>
                  <td className="px-3 py-2 text-sm tabular-nums font-semibold text-emerald-700 dark:text-emerald-400">{r.kg_available} kg</td>
                  <td className="px-3 py-2 text-sm tabular-nums text-stone-700 dark:text-stone-300">{money(r.avg_unit_cost)}/kg</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {entries.length > 0 && (
          <div className="px-5 py-3 border-t border-stone-200 dark:border-stone-800">
            <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-500 dark:text-stone-400 mb-1.5">Entradas registradas</div>
            <div className="flex flex-wrap gap-1.5">
              {entries.map((e) => (
                <span key={e.id} className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded bg-stone-100 dark:bg-stone-900 text-stone-700 dark:text-stone-300 border border-stone-200 dark:border-stone-800">
                  <span className="font-medium">{INSUMO_LABELS[e.code] || e.code}</span>
                  <span>{e.kg} kg @ {money(e.unit_cost)}</span>
                  <span className="text-stone-500">{e.entry_date}</span>
                  <button onClick={() => setToDelete(e)} className="text-stone-400 hover:text-red-600 ml-0.5"><Trash2 className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          </div>
        )}
      </Card>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar entrada?</AlertDialogTitle><AlertDialogDescription>La entrada de {toDelete && (INSUMO_LABELS[toDelete.code] || toDelete.code)} de {toDelete?.kg} kg será eliminada del stock.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700 text-white">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const TONES = {
  blue: "bg-[#1E3A8A]/10 text-[#1E3A8A] dark:text-blue-400",
  amber: "bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400",
  emerald: "bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400",
  stone: "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300",
};

const KPI = ({ icon: Icon, label, value, unit, tone = "blue" }) => (
  <Card className="p-5 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs font-outfit font-semibold uppercase tracking-[0.1em] text-stone-500 dark:text-stone-400">{label}</div>
        <div className="mt-2 font-outfit font-semibold text-3xl text-stone-900 dark:text-stone-100 tabular-nums">
          {value} <span className="text-base font-medium text-stone-500 dark:text-stone-400 ml-1">{unit}</span>
        </div>
      </div>
      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${TONES[tone]}`}>
        <Icon className="w-4 h-4" strokeWidth={1.75} />
      </div>
    </div>
  </Card>
);

const STATUS_BADGE = {
  disponible: "bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300",
  en_produccion: "bg-orange-100 dark:bg-orange-950/40 text-orange-800 dark:text-orange-300",
  agotado: "bg-stone-200 dark:bg-stone-800 text-stone-700 dark:text-stone-300",
};

const ORIGIN_BADGE = {
  produccion: "bg-[#1E3A8A]/10 text-[#1E3A8A] dark:bg-blue-950/40 dark:text-blue-400",
  compra: "bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300",
};

const Table = ({ title, rows, columns }) => (
  <Card className="border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm overflow-hidden">
    <div className="px-5 py-3.5 border-b border-stone-200 dark:border-stone-800"><h3 className="font-outfit font-semibold text-stone-900 dark:text-stone-100">{title}</h3></div>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-stone-100 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
          <tr>{columns.map((c) => <th key={c.k} className="px-3 py-2.5 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">{c.h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="px-4 py-6 text-center text-sm text-stone-500">Sin registros.</td></tr>
          ) : rows.map((row, i) => (
            <tr key={i} className="border-b border-stone-100 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-900/50">
              {columns.map((c) => (
                <td key={c.k} className={`px-3 py-2 text-sm ${c.num ? "tabular-nums" : ""} ${c.bold ? "font-semibold" : ""} ${c.highlight ? "font-medium text-[#1E3A8A] dark:text-blue-400" : c.class || "text-stone-700 dark:text-stone-300"}`}>
                  {c.badge ? <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-outfit font-semibold ${(c.badgeMap || STATUS_BADGE)[row[c.k]] || ""}`}>{row[c.k]?.replace("_", " ")}</span>
                    : c.num ? (row[c.k] ?? 0).toLocaleString("es-MX")
                    : row[c.k] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Card>
);
